import { useEffect, useState, useCallback } from "react";
import { HandHelping, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const STATUS_STYLES = {
  NEW: "bg-champagne-light text-champagne-dark",
  "IN REVIEW": "bg-teal-light text-teal",
  SCHEDULED: "bg-teal-light text-teal",
  "IN PROGRESS": "bg-teal-light text-teal",
  COMPLETED: "bg-sage-light text-sage",
  CANCELLED: "bg-red-950/60 text-red-300",
};

const MODES = [["In person", "in_person"], ["Video call", "virtual"], ["Phone", "phone"]];

export default function ServiceRequests() {
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState("");
  const [details, setDetails] = useState("");
  const [mode, setMode] = useState("");
  const [urgency, setUrgency] = useState("standard");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/service-requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/meta/options").then((r) => setTypes(r.data.service_request_types)).catch(() => {});
  }, [load]);

  const submit = async () => {
    if (!selected) {
      toast.error("Please choose a service first.");
      return;
    }
    setSending(true);
    try {
      await api.post("/service-requests", { service_type: selected, details, preferred_mode: mode, urgency });
      toast.success("Request sent. Your Full Circle team will follow up in your portal.");
      setSelected("");
      setDetails("");
      setMode("");
      setUrgency("standard");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the request.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-services-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Request a Service</h1>
        <p className="mt-1 text-sm text-mutedink">Need something new — a notary, ITIN help, translation, a document from abroad? Ask right here and it joins your organized journey.</p>
      </div>

      <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="service-request-form">
        <h2 className="text-base font-sans font-semibold text-navy mb-4 flex items-center gap-2"><HandHelping className="h-4 w-4 text-teal" /> What do you need?</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setSelected(t)}
              data-testid={`service-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-xl border px-3 py-3.5 text-xs sm:text-sm font-medium text-left transition-all ${selected === t ? "border-teal bg-teal-light text-teal" : "border-line bg-ivory text-navy hover:border-teal"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {selected && (
          <div className="mt-6 space-y-4 animate-fade-up" data-testid="service-request-details">
            <div>
              <Label htmlFor="sr-details">Tell us a little more (optional)</Label>
              <Textarea id="sr-details" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} className="mt-1.5" placeholder="Example: I need two pages notarized for my bank..." data-testid="service-details-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>How would you like this handled?</Label>
                <div className="mt-1.5 grid grid-cols-3 gap-2">
                  {MODES.map(([label, v]) => (
                    <button
                      key={v}
                      onClick={() => setMode(v)}
                      data-testid={`service-mode-${v}`}
                      className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-all ${mode === v ? "border-teal bg-teal-light text-teal" : "border-line bg-ivory text-navy hover:border-teal"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>How urgent is this?</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {[["Standard", "standard"], ["Urgent", "urgent"]].map(([label, v]) => (
                    <button
                      key={v}
                      onClick={() => setUrgency(v)}
                      data-testid={`service-urgency-${v}`}
                      className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-all ${urgency === v ? (v === "urgent" ? "border-champagne-dark bg-champagne-light text-champagne-dark" : "border-teal bg-teal-light text-teal") : "border-line bg-ivory text-navy hover:border-teal"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Button onClick={submit} disabled={sending} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="service-request-submit">
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</> : <>Send Request <Send className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        )}
      </section>

      <section data-testid="service-request-list">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Your requests</h2>
        {requests.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No requests yet. Anything you ask for will be tracked here with live status.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.request_id} className="bg-surface border border-line rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`service-request-row-${r.request_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy">{r.service_type}</p>
                  <p className="text-xs text-mutedink mt-0.5">
                    {new Date(r.created_at).toLocaleDateString()}
                    {r.preferred_mode && ` · ${MODES.find(([, v]) => v === r.preferred_mode)?.[0] || r.preferred_mode}`}
                    {r.urgency === "urgent" && <span className="text-champagne-dark font-semibold"> · Urgent</span>}
                  </p>
                  {r.details && <p className="text-xs text-mutedink mt-1 line-clamp-2">{r.details}</p>}
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit ${STATUS_STYLES[r.status] || "bg-sand text-mutedink"}`} data-testid={`service-request-status-${r.request_id}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
